import React, { useState, useEffect, useRef } from "react";
import {
  Alert,
  Box,
  Container,
  Header,
  Flashbar,
  Link,
  ProgressBar,
} from "@amzn/awsui-components-react";
import "./BoffinComputeProgress.css";
import { API, graphqlOperation } from "aws-amplify";
import { configureGenericAppSync } from "../../context/AuthContextUtility";
import {
  getBoffinComputesWithStartedStatus,
  getBoffinComputeStatus,
  getBoffinComputeMeta,
} from "./BoffinAPIs";

const COMPLETED_STATUS = "Processing has completed";
// The backend flips lockStatus to "Completed" in the terminal tail of every compute path — on
// success AND on a caught failure (where processStatus holds an error message). So a record that is
// no longer locked as "Started" is done, whatever its processStatus/percentage. This is the most
// reliable dismissal signal; the processStatus/percentage check remains for live events, whose
// subscription payload does not carry lockStatus.
const STARTED_LOCK_STATUS = "Started";
const isTerminalLockStatus = (lockStatus) =>
  Boolean(lockStatus) && lockStatus !== STARTED_LOCK_STATUS;
// Poll the DB this often, only while a compute is shown, as a backstop for a dropped
// terminal subscription event. Live events remain the primary driver of the bar.
const RECONCILE_INTERVAL_MS = 10000;

// Members of this team can release a stuck compute lock from Boffin → Unlock Compute. Shown in the
// banner so a user blocked by a hung run knows who to ask, with a 15-minute threshold so a slow
// but healthy run isn't unlocked (which would let another run start on top of it).
const BOFFIN_SUPER_USERS_GROUP = "Boffin-Super-Users";
const BOFFIN_SUPER_USERS_URL =
  "https://access.a2z.com/group-management/team/Boffin-Super-Users";

const isComplete = (processStatus, percentageCompleted) =>
  processStatus === COMPLETED_STATUS || percentageCompleted === 100;

// Map the record's latestRequestType marker to a display label + badge color. latestRequestType is
// a UI-only field the submitting page sets:
//   "Compute"            → a brand new compute
//   "Recompute"          → an OnScreen Override recompute
//   "OverrideFileUpload" → an override submitted via file upload (Edit Item)
const REQUEST_TYPES = {
  Compute: { label: "Compute", color: "blue" },
  Recompute: { label: "Recompute", color: "purple" },
  OverrideFileUpload: { label: "Override file upload", color: "grey" },
};
const DEFAULT_REQUEST_TYPE = REQUEST_TYPES.Compute;

// Resolve the badge for a record. Prefer the UI-only latestRequestType field; fall back to the
// legacy recompute flag ("Yes") for records created before latestRequestType existed, else Compute.
const requestTypeFor = (record) => {
  if (record.latestRequestType && REQUEST_TYPES[record.latestRequestType]) {
    return REQUEST_TYPES[record.latestRequestType];
  }
  if (record.recompute === "Yes") {
    return REQUEST_TYPES.Recompute;
  }
  return DEFAULT_REQUEST_TYPE;
};

// Shape the banner's data from a compute record (metadata) plus its current status/percentage.
// startDate drives Started/Elapsed: prefer lastStartedDate (this run's start) so a recompute, which
// reuses the record, doesn't show Elapsed since the ORIGINAL createDate. Falls back to createDate
// for records created before lastStartedDate existed.
const buildActiveFromRecord = (record) => ({
  id: record.id,
  processStatus: record.processStatus,
  percentageCompleted: parseInt(record.percentageCompleted) || 0,
  userId: record.userId,
  startDate: record.lastStartedDate || record.createDate,
  periodMonth: record.periodMonth,
  periodYear: record.periodYear,
  requestType: requestTypeFor(record),
});

const formatElapsed = (fromDate, nowMs) => {
  const start = new Date(fromDate).getTime();
  if (isNaN(start)) {
    return "-";
  }
  const totalSeconds = Math.max(0, Math.floor((nowMs - start) / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) {
    return `${h}h ${m}m ${s}s`;
  }
  if (m > 0) {
    return `${m}m ${s}s`;
  }
  return `${s}s`;
};

const formatStarted = (fromDate) => {
  const d = new Date(fromDate);
  if (isNaN(d.getTime())) {
    return "-";
  }
  return d.toLocaleTimeString();
};

/**
 * Self-contained "Now processing" banner for the currently running Boffin compute.
 *
 * Shows a single in-progress compute (lockStatus = "Started") with its metadata and a live
 * progress bar. Renders nothing when no compute is running. Reusable across pages — drop in
 * <BoffinComputeProgress /> with no props.
 *
 * Data sources, in order of authority:
 *  1. Live AppSync subscription (primary) — drives the bar/status in real time.
 *  2. DB seed on mount/reload — shows a running compute even if earlier live events were missed.
 *  3. Clear-only DB poll (backstop) — dismisses the banner if the terminal event was dropped,
 *     without overwriting the live in-progress value.
 *
 * onComputeFinished(id) is called whenever a shown compute transitions out of "running" -- via a
 * terminal live event or the backstop poll -- so a host page (e.g. the Review table) can refresh
 * that row instead of being left showing whatever status it had on initial load.
 */
const BoffinComputeProgress = ({ onComputeFinished } = {}) => {
  const [activeCompute, setActiveCompute] = useState(null);
  // Ticks every second while a compute is running so the "Elapsed" value stays live.
  const [now, setNow] = useState(Date.now());
  // Tracks the id we've already requested metadata for, so the metadata effect fetches once per
  // compute even if it re-runs before the fetch resolves.
  const metaRequestedIdRef = useRef(null);
  // The id of a live-event compute we're currently verifying (or have verified), so each new id
  // from the unscoped stream is checked once rather than on every event.
  const candidateIdRef = useRef(null);
  // id -> verified-at timestamp (ms) for records recently confirmed NOT running. The subscription
  // is unscoped, so a record with no bearing on this banner (another user's posting-status or
  // approval change) can emit repeated updates; without this cache each one would re-trigger a
  // getBoffinComputeMeta fetch, since the not-running branch below otherwise releases the guard
  // unconditionally. A short TTL (rather than a permanent cache) still lets a genuine new run of
  // the same id -- e.g. a recompute -- be picked up again shortly after.
  const verifiedNotRunningRef = useRef(new Map());
  const NOT_RUNNING_CACHE_MS = 30000;

  // Fetch a compute's record by id and adopt it into the banner ONLY if it is actually running
  // (lockStatus = "Started" and not complete). Used for two cases:
  //  - a new id seen on the (unscoped) live subscription — verify before showing, since the
  //    subscription fires for every UI4BI_BoffinComputeInfo update (other users' computes, an
  //    approval reset, a posting-status change), not just running computes;
  //  - an already-shown compute whose metadata (submitter/type/started) hasn't loaded yet.
  // Reading by id — rather than scanning the lockStatus="Started" set — fills metadata even in the
  // brief window before lockStatus flips to "Started" for the current user's own run.
  const verifyAndAdoptCompute = (id) => {
    getBoffinComputeMeta(id)
      .then((record) => {
        const isRunning =
          record &&
          record.lockStatus === STARTED_LOCK_STATUS &&
          !isComplete(record.processStatus, parseInt(record.percentageCompleted));
        if (!isRunning) {
          // Not a running compute: never show it; clear it if it happened to be shown, and release
          // the guards so a genuine later run (same id or not) can be picked up. Remember this
          // verdict briefly so repeated unrelated updates for this id don't each trigger a fetch.
          verifiedNotRunningRef.current.set(id, Date.now());
          metaRequestedIdRef.current = null;
          candidateIdRef.current = null;
          setActiveCompute((prev) => {
            if (prev && prev.id === id) {
              if (onComputeFinished) {
                onComputeFinished(id);
              }
              return null;
            }
            return prev;
          });
          return;
        }
        // Confirmed running: drop any stale "not running" verdict so a genuine new run isn't
        // mistakenly skipped later.
        verifiedNotRunningRef.current.delete(id);
        setActiveCompute((prev) =>
          prev && prev.id === id
            ? {
                // Already showing this compute: keep the live status/percentage, add metadata.
                ...buildActiveFromRecord(record),
                processStatus: prev.processStatus,
                percentageCompleted: prev.percentageCompleted,
              }
            : buildActiveFromRecord(record)
        );
      })
      .catch((error) => {
        // Release the guards so a later run can retry the failed fetch.
        metaRequestedIdRef.current = null;
        candidateIdRef.current = null;
        console.warn(error);
      });
  };

  // Live subscription — primary driver. Configure the generic AppSync client first, otherwise
  // the subscription can attach before the client is ready and silently receive no events
  // (which looks like a frozen bar).
  useEffect(() => {
    let subscription;
    let isCancelled = false;
    const subscribe = async () => {
      await configureGenericAppSync();
      const mQry = `
            subscription MySubscription {
                onUpdateUI4BI_BoffinComputeInfo {
                id
                processStatus
                percentageCompleted
                }
            }
            `;
      // Unmounted during the await above. subscription is still undefined here (it isn't assigned
      // until the subscribe() call below) and the cleanup function already ran, so there is
      // nothing to tear down -- just bail out instead of subscribing.
      if (isCancelled) {
        return;
      }
      subscription = API.graphql(graphqlOperation(mQry)).subscribe({
        next: ({ value }) => {
          const id = value.data.onUpdateUI4BI_BoffinComputeInfo.id;
          const status = value.data.onUpdateUI4BI_BoffinComputeInfo.processStatus;
          const pc = parseInt(
            value.data.onUpdateUI4BI_BoffinComputeInfo.percentageCompleted
          );
          if (isComplete(status, pc)) {
            setActiveCompute((prev) => {
              if (prev && prev.id === id) {
                if (onComputeFinished) {
                  onComputeFinished(id);
                }
                return null;
              }
              return prev;
            });
            return;
          }
          // We only update the compute we are already showing here (pure updater). A new id from
          // the (unscoped) stream is NOT adopted directly — the subscription fires for every
          // UI4BI_BoffinComputeInfo update, so we verify it is a running compute first.
          let matchedActive = false;
          setActiveCompute((prev) => {
            if (prev && prev.id === id) {
              matchedActive = true;
              return { ...prev, processStatus: status, percentageCompleted: pc };
            }
            return prev;
          });
          // New candidate id: verify (via the lockStatus-gated meta fetch) before showing. Guard so
          // each candidate is verified once, and skip ids recently confirmed not running so a busy
          // unrelated record doesn't re-trigger a fetch on every one of its updates.
          const verifiedAt = verifiedNotRunningRef.current.get(id);
          const recentlyVerifiedNotRunning =
            verifiedAt !== undefined && Date.now() - verifiedAt < NOT_RUNNING_CACHE_MS;
          if (!matchedActive && !recentlyVerifiedNotRunning && candidateIdRef.current !== id) {
            candidateIdRef.current = id;
            verifyAndAdoptCompute(id);
          }
        },
        error: (error) => console.warn(error),
      });
    };
    subscribe();
    return () => {
      isCancelled = true;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  // Side effect (not inside a state updater, which must stay pure): when a compute is shown but its
  // metadata hasn't been loaded yet — e.g. it first appeared from a bare live event — fetch the
  // record by id to fill in submitter/started/type. Guarded by a ref so it runs once per compute.
  useEffect(() => {
    // Reset guards when the banner clears so a later run — including a recompute that reuses the
    // same computeId — is verified/fetched again instead of being skipped by the id guards.
    if (!activeCompute || !activeCompute.id) {
      metaRequestedIdRef.current = null;
      candidateIdRef.current = null;
      return;
    }
    if (activeCompute.requestType) {
      return;
    }
    if (metaRequestedIdRef.current === activeCompute.id) {
      return;
    }
    metaRequestedIdRef.current = activeCompute.id;
    verifyAndAdoptCompute(activeCompute.id);
  }, [activeCompute && activeCompute.id, activeCompute && activeCompute.requestType]);

  // Seed from the DB on mount/reload so a running compute shows even if live events were missed.
  useEffect(() => {
    const loadActiveCompute = async () => {
      try {
        const started = await getBoffinComputesWithStartedStatus();
        const running = (started || []).find(
          (c) => c.processStatus !== COMPLETED_STATUS
        );
        if (running) {
          setActiveCompute(buildActiveFromRecord(running));
        }
      } catch (error) {
        console.warn(error);
      }
    };
    loadActiveCompute();
  }, []);

  // Clear-only backstop poll: dismiss the banner if the terminal event was dropped. Runs only
  // while a compute is shown; never overwrites the live in-progress value.
  useEffect(() => {
    if (!activeCompute || !activeCompute.id) {
      return undefined;
    }
    const activeId = activeCompute.id;
    const interval = setInterval(async () => {
      try {
        const compute = await getBoffinComputeStatus(activeId);
        if (!compute) {
          return;
        }
        // Dismiss when the run is terminal: either the status/percentage says complete, or the
        // backend has released the lock (covers caught failures whose status is an error message).
        if (
          isComplete(compute.processStatus, parseInt(compute.percentageCompleted)) ||
          isTerminalLockStatus(compute.lockStatus)
        ) {
          setActiveCompute((prev) => {
            if (prev && prev.id === activeId) {
              if (onComputeFinished) {
                onComputeFinished(activeId);
              }
              return null;
            }
            return prev;
          });
        }
      } catch (error) {
        console.warn(error);
      }
    }, RECONCILE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [activeCompute && activeCompute.id]);

  // Tick the elapsed timer while a compute is running.
  useEffect(() => {
    if (!activeCompute || !activeCompute.id) {
      return undefined;
    }
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [activeCompute && activeCompute.id]);

  if (!activeCompute) {
    return null;
  }

  const period = [activeCompute.periodMonth, activeCompute.periodYear]
    .filter(Boolean)
    .join("-");

  // requestType is set only once the record's metadata has been fetched (buildActiveFromRecord).
  // A bare live event sets id/status/percentage without it, so use its presence as the "metadata
  // loaded" signal and hold the metadata row until then — otherwise the badge flashes the default
  // "Compute" for a moment before switching to the real type (e.g. "Override file upload").
  const metaLoaded = Boolean(activeCompute.requestType);
  const requestType = activeCompute.requestType || DEFAULT_REQUEST_TYPE;

  return (
    <Container
      header={
        <Header variant="h2" description="Live status of the request">
          Now processing
        </Header>
      }
    >
      {metaLoaded ? (
        <div style={{ display: "flex", width: "100%", gap: "24px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Box variant="awsui-key-label">Request type</Box>
            <Box>
              <span
                className={
                  "boffin-request-badge boffin-request-badge-" + requestType.color
                }
              >
                {requestType.label}
              </span>
            </Box>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Box variant="awsui-key-label">Submitted by</Box>
            <Box>
              {activeCompute.userId ? (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={"https://phonetool.amazon.com/users/" + activeCompute.userId}
                >
                  {activeCompute.userId}
                </a>
              ) : (
                "-"
              )}
            </Box>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Box variant="awsui-key-label">Compute ID</Box>
            <Box>{activeCompute.id}</Box>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Box variant="awsui-key-label">Started</Box>
            <Box>{formatStarted(activeCompute.startDate)}</Box>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Box variant="awsui-key-label">Elapsed</Box>
            <Box>{formatElapsed(activeCompute.startDate, now)}</Box>
          </div>
        </div>
      ) : (
        <Box variant="awsui-key-label">Loading request details…</Box>
      )}
      <br />
      <div className="boffin-compute-progress-bar">
        <Flashbar
          items={[
            {
              content: (
                <ProgressBar
                  value={activeCompute.percentageCompleted}
                  variant="flash"
                  additionalInfo=""
                  description={activeCompute.processStatus}
                  label={
                    !metaLoaded
                      ? ""
                      : period
                      ? requestType.label + " · " + period
                      : requestType.label
                  }
                />
              ),
              id: "boffin_compute_progress_bar",
            },
          ]}
        />
      </div>
      <Box padding={{ top: "s" }}>
        <Alert type="info" header="Compute stuck for more than 15 minutes?">
          Members of{" "}
          <Link external href={BOFFIN_SUPER_USERS_URL}>
            {BOFFIN_SUPER_USERS_GROUP}
          </Link>{" "}
          can unlock it from Boffin → Unlock Compute. Only ask once it has been running for more
          than 15 minutes: unlocking a compute that is still running lets another run start on top
          of it.
        </Alert>
      </Box>
    </Container>
  );
};

export default BoffinComputeProgress;
